import React, { memo, useMemo } from 'react';
import PropTypes from 'prop-types';
import { TitleDivider, Table, Link, Icon, Text, useTheme } from '@Sage/hoi-poi-ui';

import { getLiteral } from 'utils/getLiteral';

const getClickPercentageColor = (percentage, theme) => {
    if (percentage <= 35) {
        return theme.colors.semantic.negative500;
    }
    if (percentage <= 65) {
        return theme.colors.semantic.focus500;
    }
    return theme.colors.semantic.positive500;
};

const LinkPerformance = memo(({ links = [] }) => {
    const theme = useTheme();

    const columns = useMemo(
        () => [
            {
                label: getLiteral('label_marketing_campaigns_metrics_link'),
                id: 'link',
                align: 'left',
                headerAlign: 'left',
                style: {
                    flex: '1 1 0%',
                    minWidth: 0,
                    overflow: 'hidden',
                },
            },
            {
                label: getLiteral('label_marketing_campaigns_metrics_unique_clicks'),
                id: 'uniqueClicks',
                align: 'right',
                headerAlign: 'right',
                style: {
                    flex: '0 0 160px',
                    width: 160,
                    minWidth: 0,
                    overflow: 'hidden',
                },
            },
            {
                label: getLiteral('label_marketing_campaigns_metrics_percent_clicks'),
                id: 'percentClicks',
                align: 'right',
                headerAlign: 'right',
                style: {
                    flex: '0 0 200px',
                    width: 200,
                    minWidth: 0,
                    overflow: 'hidden',
                },
            },
        ],
        [],
    );

    const rows = useMemo(() => {
        return links.map((link) => ({
            link: (
                <Link
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.url}
                    variation="primary"
                    className="fm-link-performance__link-anchor"
                >
                    <span className="fm-link-performance__link-text">{link.url}</span>
                    <Icon
                        name="launch"
                        size="smallMedium"
                        color={theme.colors.grey[700]}
                        className="fm-link-performance__link-icon"
                    />
                </Link>
            ),
            uniqueClicks: (
                <Text type="body" align="center">
                    {link.uniqueClicks.toLocaleString()}
                </Text>
            ),
            percentClicks: (
                <div className="fm-link-performance__percent-cell">
                    <div className="fm-link-performance__bar-track">
                        <div
                            className="fm-link-performance__bar-fill"
                            style={{
                                width: `${link.clickPercentage}%`,
                                backgroundColor: getClickPercentageColor(
                                    link.clickPercentage,
                                    theme,
                                ),
                            }}
                        />
                    </div>
                    <Text type="body" className="fm-link-performance__percent-value">
                        {`${link.clickPercentage.toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                        })} %`}
                    </Text>
                </div>
            ),
        }));
    }, [links, theme]);

    if (!links.length) {
        return null;
    }

    return (
        <div className="fm-link-performance">
            <TitleDivider type="subtitle">
                {getLiteral('label_marketing_campaigns_metrics_link_performance')}
            </TitleDivider>
            <div className="fm-link-performance__table-container">
                <Table
                    columns={columns}
                    rows={rows}
                    withHeaders
                    isHeaderHighlighted
                    containerClass="fm-link-performance__table"
                    headerClass="fm-link-performance__table-header-row"
                />
            </div>
        </div>
    );
});

LinkPerformance.displayName = 'LinkPerformance';

LinkPerformance.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string.isRequired,
            uniqueClicks: PropTypes.number.isRequired,
            clickPercentage: PropTypes.number.isRequired,
        }),
    ),
};

export default LinkPerformance;
